/**
 * Debounced hook for real-time expression validation via the backend
 * sqlglot-powered endpoint.
 *
 * Returns syntax validity, error message, and ExpressionAnalysis
 * (CAST target type, COALESCE detection, output type hint) which
 * feeds into the type compatibility checker to dynamically update
 * warnings as the user types.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { apiService, type ExpressionAnalysis, type ExpressionValidationResponse } from '../../services/api';

export interface UseExpressionValidationOptions {
  /** Debounce delay in ms (default: 500) */
  debounceMs?: number;
  /** Source file context for column resolution */
  sourceFile?: string;
}

export interface UseExpressionValidationResult {
  /** Current validation result (null if not yet validated) */
  result: ExpressionValidationResponse | null;
  /** Whether a validation request is in flight */
  isValidating: boolean;
  /** Trigger validation for an expression (debounced) */
  validate: (expression: string) => void;
  /** Clear the current result */
  clear: () => void;
}

export function useExpressionValidation(
  opts: UseExpressionValidationOptions = {}
): UseExpressionValidationResult {
  const { debounceMs = 500, sourceFile } = opts;

  const [result, setResult] = useState<ExpressionValidationResponse | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastExprRef = useRef<string>('');
  const abortRef = useRef<AbortController | null>(null);

  const validate = useCallback(
    (expression: string) => {
      // Clear any pending timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      // Empty expression — clear result
      if (!expression || !expression.trim()) {
        setResult(null);
        setIsValidating(false);
        lastExprRef.current = '';
        return;
      }

      // Same expression — skip
      if (expression === lastExprRef.current) return;

      lastExprRef.current = expression;
      setIsValidating(true);

      timerRef.current = setTimeout(async () => {
        // Cancel any in-flight request
        if (abortRef.current) {
          abortRef.current.abort();
        }
        abortRef.current = new AbortController();

        try {
          const response = await apiService.validateExpression(
            expression,
            [],
            sourceFile
          );
          // Only update if this is still the latest request
          if (expression === lastExprRef.current) {
            setResult(response);
            setIsValidating(false);
          }
        } catch (err: unknown) {
          const errObj = err as Record<string, unknown> | null;
          if (errObj?.name !== 'AbortError' && expression === lastExprRef.current) {
            setResult({
              valid: false,
              error: (typeof errObj?.message === 'string' ? errObj.message : null) || 'Validation request failed',
            });
            setIsValidating(false);
          }
        }
      }, debounceMs);
    },
    [debounceMs, sourceFile]
  );

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setResult(null);
    setIsValidating(false);
    lastExprRef.current = '';
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  return { result, isValidating, validate, clear };
}
